import type { DayNumber } from "../config/fastpass.config";
import type { Sale } from "./types";

export const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;
const JST_OFFSET_MS = 9 * HOUR_MS;

export type HourlySalesStatus = {
  hour: number;
  hourStartMs: number;
  soldCount: number;
  limit: number;
  overage: number;
};

export type HourlyLimitConfirmation = {
  hourStartMs: number;
  soldCount: number;
};

export function getJstHourStartMs(nowMs: number): number {
  return Math.floor((nowMs + JST_OFFSET_MS) / HOUR_MS) * HOUR_MS - JST_OFFSET_MS;
}

export function getJstDayStartMs(nowMs: number): number {
  return Math.floor((nowMs + JST_OFFSET_MS) / DAY_MS) * DAY_MS - JST_OFFSET_MS;
}

export function getJstHour(nowMs: number): number {
  return new Date(nowMs + JST_OFFSET_MS).getUTCHours();
}

export function formatHourlyRange(hour: number): string {
  const nextHour = (hour + 1) % 24;
  return `${String(hour).padStart(2, "0")}:00～${String(nextHour).padStart(2, "0")}:00`;
}

export function countSalesInHour(
  sales: Sale[],
  workspaceId: string,
  hourStartMs: number,
): number {
  return sales
    .filter((sale) =>
      sale.workspaceId === workspaceId
      && sale.purchasedAtMs >= hourStartMs
      && sale.purchasedAtMs < hourStartMs + HOUR_MS)
    .reduce((sum, sale) => sum + sale.quantity, 0);
}

export function buildHourlySalesStatuses(
  sales: Sale[],
  workspaceId: string,
  dayNumber: DayNumber,
  nowMs: number,
  limit: number,
): HourlySalesStatus[] {
  const dayStartMs = getJstDayStartMs(nowMs);
  return Array.from({ length: 24 }, (_, hour) => {
    const hourStartMs = dayStartMs + hour * HOUR_MS;
    const soldCount = sales
      .filter((sale) =>
        sale.workspaceId === workspaceId
        && sale.dayNumber === dayNumber
        && sale.purchasedAtMs >= hourStartMs
        && sale.purchasedAtMs < hourStartMs + HOUR_MS)
      .reduce((sum, sale) => sum + sale.quantity, 0);
    return {
      hour,
      hourStartMs,
      soldCount,
      limit,
      overage: Math.max(0, soldCount - limit),
    };
  });
}

export function validateHourlyLimitConfirmation(
  confirmation: HourlyLimitConfirmation | undefined,
  hourStartMs: number,
  soldCount: number,
  quantity: number,
  limit: number,
): "NOT_REQUIRED" | "CONFIRMED" | "REQUIRED" | "STALE" {
  if (soldCount + quantity <= limit) return "NOT_REQUIRED";
  if (!confirmation) return "REQUIRED";
  if (confirmation.hourStartMs !== hourStartMs || confirmation.soldCount !== soldCount) return "STALE";
  return "CONFIRMED";
}
